import {
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Package, Offerings } from "@revenuecat/purchases-js";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Layout } from "../components";
import { useRevenueCat } from "../contexts/RevenueCatContext";
import { useSnackbar } from "../contexts/SnackbarContext";
import { logger } from "../services";
import { RevenueCatService } from "../services/revenuecat.service";

interface PlanFeature {
  text: string;
  included: boolean;
  color?: "success" | "primary" | "warning" | "error" | "info" | "text.disabled";
}

interface PlanData {
  name: string;
  description: string;
  features: PlanFeature[];
  popular?: boolean;
}

const PLAN_DATA: Record<string, PlanData> = {
  free: {
    name: "Free",
    description: "Perfect for trying out FitCheckr",
    features: [
      { text: "2 AI fit checks per day", included: true, color: "success" },
      { text: "Basic fit visualization", included: true, color: "success" },
      { text: "5MB image upload limit", included: true, color: "success" },
      { text: "Standard processing speed", included: true, color: "success" },
      { text: "Priority customer support", included: false, color: "text.disabled" },
    ],
  },
  plus: {
    name: "FitCheckr Plus",
    description: "Unlimited AI-powered fashion visualization",
    features: [
      { text: "Unlimited AI fit checks", included: true, color: "success" },
      { text: "Advanced fit visualization", included: true, color: "success" },
      { text: "50MB image upload limit", included: true, color: "success" },
      { text: "Priority processing", included: true, color: "success" },
      { text: "Priority customer support", included: true, color: "success" },
    ],
    popular: true,
  },
};

const Subscription: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const { isPlusPlan, refreshCustomerInfo, isLoading: contextLoading } = useRevenueCat();

  const [offerings, setOfferings] = useState<Offerings | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const revenueCatService = useMemo(() => new RevenueCatService(), []);

  const loadOfferings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await revenueCatService.getOfferings();

      if (result.success && result.offerings) {
        setOfferings(result.offerings);
      } else {
        setError(result.error || "Failed to load subscription plans");
      }
    } catch (err) {
      await logger.error("subscription_offerings_load_failed", err as Error);
      setError(err instanceof Error ? err.message : "Failed to load subscription plans");
    } finally {
      setLoading(false);
    }
  }, [revenueCatService]);

  // Log page view on mount
  useEffect(() => {
    logger.userAction("subscription_page_view", {
      is_plus_plan: isPlusPlan,
    });
  }, [isPlusPlan]);

  useEffect(() => {
    if (!contextLoading) {
      loadOfferings();
    }
  }, [contextLoading, loadOfferings]);

  const handlePurchase = async (pkg: Package) => {
    try {
      setPurchasing(pkg.identifier);

      await logger.userAction("subscribe_button_clicked", { package_identifier: pkg.identifier });

      const result = await revenueCatService.purchaseProduct(pkg.identifier);

      if (result.success) {
        await logger.conversion("subscription_purchased", 8, undefined, { package_identifier: pkg.identifier });

        await logger.userAction("subscription_purchase_success", { package_identifier: pkg.identifier });

        showSnackbar({
          message: "🎉 Subscription activated successfully! Welcome to FitCheckr Plus!",
          severity: "success",
          actionLabel: "Awesome!",
        });

        await refreshCustomerInfo();
      } else {
        await logger.userAction("subscription_purchase_failed", {
          package_identifier: pkg.identifier,
          error: result.error || "Unknown error",
        });

        showSnackbar({
          message: result.error || "Purchase failed. Please try again.",
          severity: "error",
          actionLabel: "OK",
        });
      }
    } catch (err) {
      await logger.error("subscription_purchase_error", err as Error, {
        package_identifier: pkg.identifier,
      });

      await logger.userAction("subscription_purchase_failed", {
        package_identifier: pkg.identifier,
        error: err instanceof Error ? err.message : "Unknown error",
      });

      showSnackbar({
        message: err instanceof Error ? err.message : "Purchase failed. Please try again.",
        severity: "error",
        actionLabel: "OK",
      });
    } finally {
      setPurchasing(null);
    }
  };

  const handleRestorePurchases = async () => {
    try {
      setPurchasing("restore");

      await revenueCatService.restorePurchases();
      await refreshCustomerInfo();

      showSnackbar({
        message: "Purchases restored successfully!",
        severity: "success",
        actionLabel: "Great!",
      });
    } catch (err) {
      await logger.error("subscription_restore_failed", err as Error);
      showSnackbar({
        message: "Failed to restore purchases. Please try again.",
        severity: "error",
        actionLabel: "OK",
      });
    } finally {
      setPurchasing(null);
    }
  };

  const formatPrice = (pkg: Package): string => {
    const product = pkg.webBillingProduct;
    const price = product.currentPrice;

    return `${price.currency} ${price.formattedPrice}`;
  };

  const getPeriodText = (pkg: Package): string => {
    const product = pkg.webBillingProduct;
    const duration = product.normalPeriodDuration;

    if (duration?.includes("P1M")) {
      return "/month";
    }
    if (duration?.includes("P1Y")) {
      return "/year";
    }
    if (duration?.includes("P1W")) {
      return "/week";
    }

    return "";
  };

  if (contextLoading || loading) {
    return (
      <Layout title="Subscription">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
            <CircularProgress size={64} />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Subscription">
      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Choose Your Plan
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: "auto" }}>
            Unlock unlimited fashion visualization with FitCheckr.ai
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 1 }}
            action={
              <Button color="inherit" size="small" onClick={loadOfferings}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Plans Layout */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 2 }}>
          {/* Free Plan */}
          <Box sx={{ flex: 1 }}>
            <Card
              sx={{
                height: "100%",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                position: "relative",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {PLAN_DATA.free.name}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                    Free
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {PLAN_DATA.free.description}
                  </Typography>
                </Box>

                <List sx={{ mb: 2 }} dense>
                  {PLAN_DATA.free.features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon
                          sx={{
                            fontSize: 16,
                            color: `${feature.color}.main`,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.text}
                        primaryTypographyProps={{
                          variant: "body2",
                          sx: {
                            color: `${feature.color}.main`,
                            textDecoration: feature.included ? "none" : "line-through",
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Button
                  variant="outlined"
                  fullWidth
                  disabled={!isPlusPlan}
                  size="small"
                  sx={{ py: 1, borderRadius: 1 }}
                >
                  {!isPlusPlan ? "Current Plan" : "Free Plan"}
                </Button>
              </CardContent>
            </Card>
          </Box>

          {/* Plus Plan */}
          <Box sx={{ flex: 1 }}>
            {offerings?.current?.availablePackages.map((pkg) => (
              <Card
                key={pkg.identifier}
                sx={{
                  height: "100%",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderRadius: 1,
                  position: "relative",
                  boxShadow: 2,
                }}
              >
                {PLAN_DATA.plus.popular && (
                  <Chip
                    label="Popular"
                    color="primary"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      height: 20,
                      fontSize: "0.75rem",
                    }}
                  />
                )}

                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ textAlign: "center", mb: 2 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                      {PLAN_DATA.plus.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.5 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                        {formatPrice(pkg)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {getPeriodText(pkg)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {PLAN_DATA.plus.description}
                    </Typography>
                  </Box>

                  <List sx={{ mb: 2 }} dense>
                    {PLAN_DATA.plus.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckIcon sx={{ fontSize: 16, color: `${feature.color || "success"}.main` }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature.text}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handlePurchase(pkg)}
                    disabled={purchasing !== null || isPlusPlan}
                    startIcon={purchasing === pkg.identifier ? <CircularProgress size={16} /> : undefined}
                    size="small"
                    sx={{ py: 1, borderRadius: 1 }}
                  >
                    {purchasing === pkg.identifier
                      ? "Processing..."
                      : isPlusPlan
                        ? "Active"
                        : "Upgrade Now"
                    }
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Restore Purchases */}
        <Box sx={{ textAlign: "center", py: 1 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Already purchased? Restore your subscription
          </Typography>
          <Button
            variant="text"
            size="small"
            startIcon={purchasing === "restore" ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
            onClick={handleRestorePurchases}
            disabled={purchasing !== null}
            sx={{ borderRadius: 1 }}
          >
            {purchasing === "restore" ? "Restoring..." : "Restore Purchases"}
          </Button>
        </Box>
      </Container>
    </Layout>
  );
};

export default Subscription;
